import React from 'react';
import { RefreshCcw, ChevronRight } from 'lucide-react';
import { Skeleton } from '@/components/loading/Skeleton';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import TabsCard from '@/components/Tabs';
import MoreNewsDialog from './MoreNews';
import marketIcon from '@/imgs/market-icon.png';
import { usName, hsName, hkName } from './constant';
import { useMarketOverview } from '../hooks';
// interface StockData {
//   序号: number;
//   代码: string;
//   涨跌额: number;
//   昨收价: number;
//   最低价: number;
//   最新价: number;
//   名称: string;
//   振幅: number;
//   最高价: number;
//   开盘价: number;
//   最新行情时间: string;
//   涨跌幅: number;
//   table?: {
//     latest: Array<number>;
//     changeRatio: Array<number>;
//   };
//   marketCategory?: string;
//   thscode?: string;
// }

const MarketOverview: React.FC = () => {
  const markets = [
    { label: '沪深', value: 'hs' },
    { label: '港股', value: 'hk' },
    { label: '美股', value: 'us' }
  ];

  const {
    activeIndex,
    marketData,
    newsData,
    loading,
    newsLoading,
    dialogOpen,
    setActiveIndex,
    setDialogOpen,
    handleMarketRefresh,
    handleNewsRefresh,
    formatRelativeTime,
    handleToLink
  } = useMarketOverview({
    markets,
    usName,
    hsName,
    hkName
  });

  const renderMarketData = () => {
    if (loading) {
      return (
        <div className="relative w-full">
          <div className="flex h-[160px] w-full items-center">
            <div className="w-full rounded-lg border border-[#F0F0F0] p-3">
              <div className="flex gap-2">
                <Skeleton className="h-20 w-1/3" />
                <Skeleton className="h-20 w-1/3" />
                <Skeleton className="h-20 w-1/3" />
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (marketData.length === 0) {
      return (
        <div className="flex h-[120px] w-full items-center">
          <div className="w-full rounded-lg p-3">
            <div className="space-y-2">暂无数据</div>
          </div>
        </div>
      );
    }

    const isValidate = (value: number | undefined | null) => {
      return value !== undefined && value !== null;
    };

    return (
      <div className="grid grid-cols-3 gap-2">
        {marketData.map((stock, index) => {
          const isPositive = isValidate(stock.涨跌幅)
            ? stock.涨跌幅 > 0
            : stock.table?.changeRatio?.[0] > 0;
          const isZero = isValidate(stock.涨跌幅)
            ? stock.涨跌幅 === 0
            : stock.table?.changeRatio?.[0] === 0;
          const isMainIndex = index < 3;

          return (
            <div
              key={index}
              className={`rounded-md p-2 text-center ${
                isZero ? 'bg-gray-50' : isPositive ? 'bg-red-50' : 'bg-green-50'
              }`}
            >
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div
                      className="truncate text-sm text-gray-800"
                      title={stock.名称 || stock.thscode}
                    >
                      {stock.名称 || stock.thscode}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs">{stock.名称 || stock.thscode}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              {isMainIndex && (
                <div
                  className={`ml-[-5px] scale-[0.85] text-lg font-medium xs-453:ml-0 xs-453:scale-100 ${
                    isZero ? 'text-gray-600' : isPositive ? 'text-red-600' : 'text-green-600'
                  }`}
                >
                  {(stock.最新价 || stock.table?.latest?.[0] || 0).toLocaleString()}
                </div>
              )}
              <div
                className={`text-sm ${
                  isZero ? 'text-gray-600' : isPositive ? 'text-red-600' : 'text-green-600'
                }`}
              >
                {isPositive ? '+' : ''}
                {(stock.涨跌幅 || stock.table?.changeRatio?.[0] || 0).toFixed(2)}%
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <>
      {!dialogOpen ? (
        <div className="space-y-3">
          {/* 标题区域 */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1">
              <div className="h-6 w-6">
                <img src={marketIcon} alt="市场" className="h-full w-full object-cover" />
              </div>
              <span className="text-base font-semibold text-black">市场动态</span>
            </div>
            <div className="h-4 w-4 cursor-pointer" onClick={handleMarketRefresh}>
              <RefreshCcw className="h-full w-full" />
            </div>
          </div>

          <div className="rounded-lg bg-white p-3">
            <div className="rounded-lg border border-gray-100 p-3">
              <div className="mb-3 flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-800">今日大盘指数</span>
                {/* 指数选择器 */}
                <TabsCard
                  tabs={markets}
                  activeIndex={activeIndex}
                  onChange={(_, index) => setActiveIndex(index)}
                />
              </div>
              {renderMarketData()}
            </div>
          </div>

          {/* 今日热门资讯 */}
          <div className="rounded-lg bg-white p-3">
            <div className="rounded-lg border border-gray-100 p-3">
              <div className="mb-3 flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-800">今日热门资讯</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-800">实时更新</span>
                  <div className="h-4 w-4 cursor-pointer" onClick={handleNewsRefresh}>
                    <RefreshCcw className="h-full w-full" />
                  </div>
                </div>
              </div>

              {newsLoading ? (
                <div className="relative w-full">
                  <div className="flex h-[240px] w-full items-center">
                    <div className="w-full rounded-lg border border-[#F0F0F0] p-3">
                      <div className="space-y-2">
                        <Skeleton className="h-12 w-full" />
                        <div className="flex gap-2">
                          <Skeleton className="h-5 w-16" />
                          <Skeleton className="h-5 w-16" />
                          <Skeleton className="h-5 w-16" />
                        </div>
                        <Skeleton className="h-7 w-full" />
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  {newsData.slice(0, 3).map((item: any, index: number) => (
                    <div
                      key={index}
                      className="mb-4 flex flex-col gap-2 rounded-md bg-purple-50 px-3 py-2"
                    >
                      <div>
                        <div className="flex items-center gap-2">
                          <div className="min-w-0 flex-1 text-sm">
                            <div>
                              <Button
                                variant="link"
                                onClick={() => handleToLink(item.link)}
                                className="h-auto w-full p-0"
                              >
                                <div
                                  className="w-full truncate text-left"
                                  style={{ color: 'rgba(89, 89, 235, 1)' }}
                                >
                                  {item.title}
                                </div>
                              </Button>
                            </div>
                          </div>
                          <div className="whitespace-nowrap text-xs text-gray-500">
                            {formatRelativeTime(item.public_time)}
                          </div>
                        </div>
                      </div>
                      <div className="text-[rgb(31, 31, 31)] text-left text-sm">{item.content}</div>
                      <div className="text-left text-xs text-[rgb(140,140,140)]">
                        <span>来源：</span>
                        <span>{item.source}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* 查看更多 */}
              {newsData.length > 3 && (
                <div
                  className="mt-3 flex cursor-pointer items-center justify-center gap-2"
                  onClick={() => setDialogOpen(true)}
                >
                  <span className="text-sm text-gray-800">查看更多资讯</span>
                  <div className="h-4 w-4 cursor-pointer">
                    <ChevronRight className="h-full w-full" />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <MoreNewsDialog newsData={newsData} setOpen={setDialogOpen} />
      )}
    </>
  );
};

export default MarketOverview;
